import { useCallback, useRef } from "react";
import type { TimelineClip, TimelineAction } from "@scroll/shared-types/timeline";
import { MIN_CLIP_DURATION } from "@scroll/shared-types/timeline";

interface TrimHandlesProps {
  clip: TimelineClip;
  pixelsPerSecond: number;
  onDispatch: (action: TimelineAction) => void;
}

// Pixels-of-cursor-travel that a candidate trim must be within before we
// snap it to the nearest source edge. Computed at drag time from the live
// pixelsPerSecond so dense timelines feel as snappy as sparse ones.
const SNAP_PX = 8;

export default function TrimHandles({
  clip,
  pixelsPerSecond,
  onDispatch,
}: TrimHandlesProps) {
  const draggingRef = useRef<"left" | "right" | null>(null);
  const startXRef = useRef(0);
  const startTrimRef = useRef({ trimStart: 0, trimEnd: 0 });
  const lastTrimRef = useRef({ trimStart: 0, trimEnd: 0 });

  const handleMouseDown = useCallback(
    (side: "left" | "right", e: React.MouseEvent) => {
      e.stopPropagation();
      draggingRef.current = side;
      startXRef.current = e.clientX;
      startTrimRef.current = { trimStart: clip.trimStart, trimEnd: clip.trimEnd };
      lastTrimRef.current = { trimStart: clip.trimStart, trimEnd: clip.trimEnd };

      // Snap threshold expressed in source-time, scaled by clip speed so it
      // feels like the same pixel distance regardless of speed.
      const snapTime = (SNAP_PX / pixelsPerSecond) * clip.speed;

      const handleMouseMove = (e: MouseEvent) => {
        const dx = e.clientX - startXRef.current;
        const dt = (dx / pixelsPerSecond) * clip.speed;
        // Shift disables snapping for precise scrubs.
        const allowSnap = !e.shiftKey;

        let trimStart = clip.trimStart;
        let trimEnd = clip.trimEnd;

        if (draggingRef.current === "left") {
          let candidate = startTrimRef.current.trimStart + dt;
          if (allowSnap && candidate < snapTime) candidate = 0;
          trimStart = Math.max(
            0,
            Math.min(candidate, startTrimRef.current.trimEnd - MIN_CLIP_DURATION * clip.speed),
          );
          trimEnd = startTrimRef.current.trimEnd;
        } else {
          let candidate = startTrimRef.current.trimEnd + dt;
          if (allowSnap && clip.sourceDuration - candidate < snapTime) {
            candidate = clip.sourceDuration;
          }
          trimStart = startTrimRef.current.trimStart;
          trimEnd = Math.min(
            clip.sourceDuration,
            Math.max(candidate, startTrimRef.current.trimStart + MIN_CLIP_DURATION * clip.speed),
          );
        }

        lastTrimRef.current = { trimStart, trimEnd };

        // Preview without undo history
        onDispatch({
          type: "TRIM_CLIP_PREVIEW",
          clipId: clip.id,
          trimStart,
          trimEnd,
        });
      };

      const cleanup = () => {
        draggingRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("keydown", handleKeyDown);
      };

      const handleMouseUp = () => {
        cleanup();

        // Only create undo entry if trim actually changed
        const { trimStart, trimEnd } = lastTrimRef.current;
        if (
          trimStart !== startTrimRef.current.trimStart ||
          trimEnd !== startTrimRef.current.trimEnd
        ) {
          // Undo the preview, then apply with history
          onDispatch({
            type: "TRIM_CLIP_PREVIEW",
            clipId: clip.id,
            trimStart: startTrimRef.current.trimStart,
            trimEnd: startTrimRef.current.trimEnd,
          });
          onDispatch({
            type: "TRIM_CLIP",
            clipId: clip.id,
            trimStart,
            trimEnd,
          });
        }
      };

      // Escape cancels the in-flight drag and reverts to where it started.
      const handleKeyDown = (ev: KeyboardEvent) => {
        if (ev.key !== "Escape") return;
        ev.preventDefault();
        onDispatch({
          type: "TRIM_CLIP_PREVIEW",
          clipId: clip.id,
          trimStart: startTrimRef.current.trimStart,
          trimEnd: startTrimRef.current.trimEnd,
        });
        lastTrimRef.current = { ...startTrimRef.current };
        cleanup();
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("keydown", handleKeyDown);
    },
    [clip, pixelsPerSecond, onDispatch],
  );

  return (
    <>
      <div
        className="timeline-trim-handle timeline-trim-handle-left"
        role="slider"
        aria-label="Trim clip start"
        aria-valuemin={0}
        aria-valuemax={clip.trimEnd}
        aria-valuenow={clip.trimStart}
        aria-valuetext={`Start at ${clip.trimStart.toFixed(2)} seconds`}
        onMouseDown={(e) => handleMouseDown("left", e)}
      >
        <div className="timeline-trim-handle-grip" />
      </div>
      <div
        className="timeline-trim-handle timeline-trim-handle-right"
        role="slider"
        aria-label="Trim clip end"
        aria-valuemin={clip.trimStart}
        aria-valuemax={clip.sourceDuration}
        aria-valuenow={clip.trimEnd}
        aria-valuetext={`End at ${clip.trimEnd.toFixed(2)} seconds`}
        onMouseDown={(e) => handleMouseDown("right", e)}
      >
        <div className="timeline-trim-handle-grip" />
      </div>
    </>
  );
}
